import React, { useState, useEffect } from 'react';
import { 
  Image, Palette, Type, Download, Loader2, 
  Upload, Trash2, RefreshCw, Sparkles, Wand2, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import './CoverArtGenerator.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CoverArtGenerator = ({ isPublic = false }) => {
  const [palettes, setPalettes] = useState({});
  const [fonts, setFonts] = useState({});
  const [imageStyles, setImageStyles] = useState({});
  const [imageEffects, setImageEffects] = useState({});
  const [textStyles, setTextStyles] = useState({});
  const [loading, setLoading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [generatedImages, setGeneratedImages] = useState(null); // { withLogo, withoutLogo }
  const [activePreview, setActivePreview] = useState('withLogo'); // which version to show
  
  // Form state
  const [description, setDescription] = useState('');
  const [artistName, setArtistName] = useState('');
  const [trackTitle, setTrackTitle] = useState('');
  const [textPosition, setTextPosition] = useState('top_center');
  const [selectedPalette, setSelectedPalette] = useState('');
  const [selectedImageStyle, setSelectedImageStyle] = useState('visual');
  const [selectedTextStyle, setSelectedTextStyle] = useState('auto');
  const [artistFontKey, setArtistFontKey] = useState('default');
  const [titleFontKey, setTitleFontKey] = useState('default');
  const [artistLogo, setArtistLogo] = useState(null);
  const [artistLogoPreview, setArtistLogoPreview] = useState(null);
  const [useArtistLogo, setUseArtistLogo] = useState(false);
  const [showTextBg, setShowTextBg] = useState(false);
  const [textBgOpacity, setTextBgOpacity] = useState(150);
  
  // Custom image upload state
  const [useCustomImage, setUseCustomImage] = useState(false);
  const [customImage, setCustomImage] = useState(null);
  const [customImagePreview, setCustomImagePreview] = useState(null);
  const [selectedEffect, setSelectedEffect] = useState('none');

  // API base path - admin or public
  const apiBase = isPublic ? '/api/cover-art' : '/api/admin/cover-art';

  useEffect(() => {
    fetchPalettes();
    fetchFonts();
    fetchImageStyles();
    fetchImageEffects();
    fetchTextStyles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getHeaders = () => {
    const headers = {};
    if (!isPublic) {
      const token = localStorage.getItem('adminToken');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return headers;
  };

  const fetchPalettes = async () => {
    try {
      const res = await fetch(`${API_URL}${apiBase}/palettes`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setPalettes(data);
      }
    } catch (error) {
      console.error('Failed to fetch palettes:', error);
    }
  };

  const fetchFonts = async () => {
    try {
      const res = await fetch(`${API_URL}${apiBase}/fonts`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setFonts(data);
      }
    } catch (error) {
      console.error('Failed to fetch fonts:', error);
    }
  };

  const fetchImageStyles = async () => {
    try {
      const res = await fetch(`${API_URL}${apiBase}/styles`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setImageStyles(data);
      }
    } catch (error) {
      console.error('Failed to fetch image styles:', error);
    }
  };

  const fetchImageEffects = async () => {
    try {
      const res = await fetch(`${API_URL}${apiBase}/effects`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setImageEffects(data);
      }
    } catch (error) {
      console.error('Failed to fetch image effects:', error);
    }
  };

  const fetchTextStyles = async () => {
    try {
      const res = await fetch(`${API_URL}${apiBase}/text-styles`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setTextStyles(data);
      }
    } catch (error) {
      console.error('Failed to fetch text styles:', error);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Logo must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setArtistLogo(base64);
        setArtistLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCustomImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be less than 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setCustomImage(base64);
        setCustomImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCustomImage = () => {
    setCustomImage(null);
    setCustomImagePreview(null);
  };

  const removeLogo = () => {
    setArtistLogo(null);
    setArtistLogoPreview(null);
  };

  const handleOptimizeDescription = async () => {
    if (!description.trim()) {
      toast.error('Please enter a description to optimize');
      return;
    }

    setOptimizing(true);
    try {
      const params = new URLSearchParams();
      params.append('description', description);

      const res = await fetch(`${API_URL}${apiBase}/optimize-description?${params}`, {
        method: 'POST',
        headers: getHeaders()
      });

      if (!res.ok) {
        throw new Error('Failed to optimize description');
      }

      const data = await res.json();
      setDescription(data.optimized_description);
      toast.success('Description optimized with AI!');
    } catch (error) {
      const errorMsg = typeof error === 'string' ? error : (error?.message || 'Failed to optimize description');
      toast.error(errorMsg);
      console.error('Optimization error:', error);
    } finally {
      setOptimizing(false);
    }
  };

  const handleGenerate = async () => {
    // For AI generation, need description. For custom image, need the image.
    if (!useCustomImage && !description.trim()) {
      toast.error('Please enter a description for the artwork');
      return;
    }
    
    if (useCustomImage && !customImage) {
      toast.error('Please upload a custom image');
      return;
    }

    setLoading(true);
    setGeneratedImages(null);

    try {
      const params = new URLSearchParams();
      
      if (useCustomImage) {
        // Process custom image
        if (!useArtistLogo && artistName) params.append('artist_name', artistName);
        if (trackTitle) params.append('track_title', trackTitle);
        params.append('text_position', textPosition);
        params.append('artist_font_key', artistFontKey);
        params.append('title_font_key', titleFontKey);
        params.append('show_text_bg', showTextBg);
        params.append('text_bg_opacity', textBgOpacity);
        params.append('image_effect', selectedEffect);
        if (selectedPalette) params.append('color_palette', selectedPalette);
        if (useArtistLogo && artistLogo) params.append('artist_logo_base64', artistLogo);
        params.append('custom_image_base64', customImage);

        const res = await fetch(`${API_URL}${apiBase}/process-custom?${params}`, {
          method: 'POST',
          headers: getHeaders()
        });

        if (!res.ok) {
          let errorMessage = 'Processing failed';
          try {
            const errorData = await res.json();
            errorMessage = errorData.detail || errorMessage;
          } catch {
            // Response wasn't JSON
          }
          throw new Error(errorMessage);
        }

        const data = await res.json();
        setGeneratedImages({
          withLogo: data.image_with_logo,
          withoutLogo: data.image_without_logo
        });
        setActivePreview('withLogo');
        toast.success('Custom image processed successfully! Two versions created.');
      } else {
        // AI generation
        params.append('description', description);
        if (!useArtistLogo && artistName) params.append('artist_name', artistName);
        if (trackTitle) params.append('track_title', trackTitle);
        params.append('text_position', textPosition);
        params.append('artist_font_key', artistFontKey);
        params.append('title_font_key', titleFontKey);
        params.append('show_text_bg', showTextBg);
        params.append('text_bg_opacity', textBgOpacity);
        params.append('image_style', selectedImageStyle);
        params.append('text_style', selectedTextStyle);
        if (selectedPalette) params.append('color_palette', selectedPalette);
        if (useArtistLogo && artistLogo) params.append('artist_logo_base64', artistLogo);

        const res = await fetch(`${API_URL}${apiBase}/generate?${params}`, {
          method: 'POST',
          headers: getHeaders()
        });

        if (!res.ok) {
          let errorMessage = 'Generation failed';
          try {
            const errorData = await res.json();
            errorMessage = errorData.detail || errorMessage;
          } catch {
            // Response wasn't JSON
          }
          throw new Error(errorMessage);
        }

        const data = await res.json();
        setGeneratedImages({
          withLogo: data.image_with_logo,
          withoutLogo: data.image_without_logo
        });
        setActivePreview('withLogo');
        toast.success('Cover art generated successfully! Two versions created.');
      }
    } catch (error) {
      // Only pass string message to toast to avoid postMessage serialization issues
      const errorMsg = typeof error === 'string' ? error : (error?.message || 'Failed to generate cover art');
      toast.error(errorMsg);
      console.error('Cover art generation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (version) => {
    if (!generatedImages) return;
    
    const imageData = version === 'withLogo' ? generatedImages.withLogo : generatedImages.withoutLogo;
    const suffix = version === 'withLogo' ? 'amr' : 'clean';
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${imageData}`;
    const filename = `${artistName || 'cover'}_${trackTitle || 'art'}_${suffix}_${Date.now()}.png`
      .replace(/\s+/g, '_').toLowerCase();
    link.download = filename;
    link.click();
    toast.success(`Cover art (${version === 'withLogo' ? 'with AMR logo' : 'clean version'}) downloaded!`);
  };

  const handleDownloadBoth = () => {
    handleDownload('withLogo');
    setTimeout(() => handleDownload('withoutLogo'), 500);
  };

  return (
    <div className={`cover-art-generator ${isPublic ? 'public-mode' : ''}`} data-testid="cover-art-generator">
      <div className="generator-header">
        <h1><Image size={28} /> Cover Art Generator</h1>
        <p>Create stunning AI-generated album artwork with the Anyone Music Records branding</p>
      </div>

      <div className="generator-content">
        {/* Left Panel - Form */}
        <div className="generator-form">
          <div className="form-section">
            <h3><Type size={18} /> Track Details</h3>
            
            <div className="form-group">
              <label>Track / Album Title *</label>
              <input
                type="text"
                value={trackTitle}
                onChange={(e) => setTrackTitle(e.target.value)}
                placeholder="Enter track or album title"
                data-testid="track-title-input"
              />
            </div>

            <div className="form-group">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={useArtistLogo}
                  onChange={(e) => setUseArtistLogo(e.target.checked)}
                />
                <span>Use Artist Logo (instead of text)</span>
              </label>
            </div>

            {useArtistLogo ? (
              <div className="form-group">
                <label>Artist Logo</label>
                {artistLogoPreview ? (
                  <div className="logo-preview">
                    <img src={artistLogoPreview} alt="Artist logo" />
                    <button onClick={removeLogo} className="remove-logo">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="logo-upload">
                    <Upload size={24} />
                    <span>Upload Logo (PNG, max 5MB)</span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleLogoUpload}
                      hidden
                    />
                  </label>
                )}
              </div>
            ) : (
              <div className="form-group">
                <label>Artist Name</label>
                <input
                  type="text"
                  value={artistName}
                  onChange={(e) => setArtistName(e.target.value)}
                  placeholder="Enter artist name"
                  data-testid="artist-name-input"
                />
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Artist Font</label>
                <select
                  value={artistFontKey}
                  onChange={(e) => setArtistFontKey(e.target.value)}
                  className="font-select"
                  data-testid="artist-font-select"
                >
                  {Object.entries(fonts).map(([key, name]) => (
                    <option key={key} value={key}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Title Font</label>
                <select
                  value={titleFontKey}
                  onChange={(e) => setTitleFontKey(e.target.value)}
                  className="font-select"
                  data-testid="title-font-select"
                >
                  {Object.entries(fonts).map(([key, name]) => (
                    <option key={key} value={key}>{name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Text Position</label>
              <div className="position-options">
                <button
                  className={`position-btn ${textPosition === 'top_left' ? 'active' : ''}`}
                  onClick={() => setTextPosition('top_left')}
                  data-testid="position-top-left"
                >
                  <div className="position-preview top-left">
                    <div className="text-lines"></div>
                  </div>
                  <span>Top Left</span>
                </button>
                <button
                  className={`position-btn ${textPosition === 'top_center' ? 'active' : ''}`}
                  onClick={() => setTextPosition('top_center')}
                  data-testid="position-top-center"
                >
                  <div className="position-preview top">
                    <div className="text-lines"></div>
                  </div>
                  <span>Top Center</span>
                </button>
                <button
                  className={`position-btn ${textPosition === 'center_center' ? 'active' : ''}`}
                  onClick={() => setTextPosition('center_center')}
                  data-testid="position-center"
                >
                  <div className="position-preview center">
                    <div className="text-lines"></div>
                  </div>
                  <span>Center</span>
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={showTextBg}
                  onChange={(e) => setShowTextBg(e.target.checked)}
                />
                <span>Add semi-transparent background behind text</span>
              </label>
            </div>

            {showTextBg && (
              <div className="form-group">
                <label>Background Opacity: {Math.round(textBgOpacity / 255 * 100)}%</label>
                <input
                  type="range"
                  min="50"
                  max="220"
                  value={textBgOpacity}
                  onChange={(e) => setTextBgOpacity(parseInt(e.target.value))}
                  className="opacity-slider"
                />
                <div className="opacity-labels">
                  <span>Light</span>
                  <span>Dark</span>
                </div>
              </div>
            )}
          </div>

          {/* Image Source Section */}
          <div className="form-section">
            <h3><Camera size={18} /> Image Source</h3>
            
            <div className="source-toggle">
              <button
                className={`source-btn ${!useCustomImage ? 'active' : ''}`}
                onClick={() => setUseCustomImage(false)}
                data-testid="source-ai"
              >
                <Sparkles size={18} />
                <span>AI Generated</span>
              </button>
              <button
                className={`source-btn ${useCustomImage ? 'active' : ''}`}
                onClick={() => setUseCustomImage(true)}
                data-testid="source-custom"
              >
                <Upload size={18} />
                <span>Upload Your Own</span>
              </button>
            </div>

            {useCustomImage ? (
              <>
                <div className="form-group">
                  <label>Upload Custom Image</label>
                  {customImagePreview ? (
                    <div className="custom-image-preview">
                      <img src={customImagePreview} alt="Custom upload" />
                      <button onClick={removeCustomImage} className="remove-image">
                        <Trash2 size={16} />
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label className="custom-upload">
                      <Upload size={32} />
                      <span>Upload Image</span>
                      <span className="upload-hint">PNG, JPG, or WebP (max 10MB)</span>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={handleCustomImageUpload}
                        hidden
                      />
                    </label>
                  )}
                </div>

                <div className="form-group">
                  <label>Image Effect</label>
                  <div className="effect-grid" data-testid="effect-selector">
                    {Object.entries(imageEffects).map(([key, effect]) => (
                      <button
                        key={key}
                        type="button"
                        className={`effect-option ${selectedEffect === key ? 'active' : ''}`}
                        onClick={() => setSelectedEffect(key)}
                        title={effect.description}
                        data-testid={`effect-${key}`}
                      >
                        <span className="effect-name">{effect.name}</span>
                      </button>
                    ))}
                  </div>
                  <p className="form-hint">Apply a filter to style your image</p>
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label>Background Description *</label>
                  <div className="description-wrapper">
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe the mood and visuals (e.g., 'underwater city with bioluminescent lights', 'cosmic nebula with geometric patterns', 'desert landscape at golden hour')"
                      rows={4}
                      data-testid="description-input"
                    />
                    <button
                      type="button"
                      className="optimize-btn"
                      onClick={handleOptimizeDescription}
                      disabled={optimizing || !description.trim()}
                      title="Use AI to enhance your description"
                    >
                      {optimizing ? (
                        <Loader2 size={16} className="spinner" />
                      ) : (
                        <Wand2 size={16} />
                      )}
                      <span>{optimizing ? 'Optimizing...' : 'AI Enhance'}</span>
                    </button>
                  </div>
                  <p className="form-hint">
                    <Sparkles size={12} /> Click "AI Enhance" to optimize your description for better results
                  </p>
                </div>

                <div className="form-group">
                  <label>AI Image Style</label>
                  <div className="style-grid" data-testid="image-style-selector">
                    {Object.entries(imageStyles).map(([key, style]) => (
                      <button
                        key={key}
                        type="button"
                        className={`style-option ${selectedImageStyle === key ? 'active' : ''}`}
                        onClick={() => setSelectedImageStyle(key)}
                        title={style.description}
                        data-testid={`style-${key}`}
                      >
                        <span className="style-name">{style.name}</span>
                        <span className="style-desc">{style.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Text Layout Style</label>
                  <div className="text-style-grid" data-testid="text-style-selector">
                    {Object.entries(textStyles).map(([key, style]) => (
                      <button
                        key={key}
                        type="button"
                        className={`text-style-option ${selectedTextStyle === key ? 'active' : ''}`}
                        onClick={() => setSelectedTextStyle(key)}
                        title={style.description}
                        data-testid={`text-style-${key}`}
                      >
                        <span className="style-name">{style.name}</span>
                        <span className="style-desc">{style.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="form-group">
              <label>Color Palette (Optional)</label>
              <div className="palette-grid">
                <button
                  className={`palette-option ${selectedPalette === '' ? 'active' : ''}`}
                  onClick={() => setSelectedPalette('')}
                >
                  <div className="palette-colors auto">
                    <RefreshCw size={16} />
                  </div>
                  <span>Auto</span>
                </button>
                {Object.entries(palettes).map(([key, palette]) => (
                  <button
                    key={key}
                    className={`palette-option ${selectedPalette === key ? 'active' : ''}`}
                    onClick={() => setSelectedPalette(key)}
                    title={palette.description}
                  >
                    <div className="palette-colors">
                      {palette.colors.map((color, i) => (
                        <div key={i} style={{ backgroundColor: color }} />
                      ))}
                    </div>
                    <span>{palette.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            className="generate-btn"
            onClick={handleGenerate}
            disabled={loading || (useCustomImage ? !customImage : !description.trim())}
            data-testid="generate-btn"
          >
            {loading ? (
              <>
                <Loader2 size={20} className="spinner" />
                {useCustomImage ? 'Processing...' : 'Generating... (this may take up to 60 seconds)'}
              </>
            ) : (
              <>
                <Image size={20} />
                {useCustomImage ? 'Process Image' : 'Generate Cover Art'}
              </>
            )}
          </button>
        </div>

        {/* Right Panel - Preview */}
        <div className="generator-preview">
          <h3>Preview</h3>
          <div className="preview-container">
            {loading ? (
              <div className="preview-loading">
                <Loader2 size={48} className="spinner" />
                <p>Creating your masterpiece...</p>
                <p className="loading-hint">AI is generating two versions - with and without AMR logo</p>
              </div>
            ) : generatedImages ? (
              <>
                <div className="version-tabs">
                  <button 
                    className={`version-tab ${activePreview === 'withLogo' ? 'active' : ''}`}
                    onClick={() => setActivePreview('withLogo')}
                    data-testid="tab-with-logo"
                  >
                    With AMR Logo
                  </button>
                  <button 
                    className={`version-tab ${activePreview === 'withoutLogo' ? 'active' : ''}`}
                    onClick={() => setActivePreview('withoutLogo')}
                    data-testid="tab-without-logo"
                  >
                    Clean (No Logo)
                  </button>
                </div>
                <img 
                  src={`data:image/png;base64,${activePreview === 'withLogo' ? generatedImages.withLogo : generatedImages.withoutLogo}`} 
                  alt={`Generated cover art ${activePreview === 'withLogo' ? 'with AMR logo' : 'without logo'}`}
                  className="preview-image"
                />
                <div className="download-buttons">
                  <button 
                    className="download-btn" 
                    onClick={() => handleDownload(activePreview)}
                    data-testid="download-current"
                  >
                    <Download size={18} />
                    Download This Version
                  </button>
                  <button 
                    className="download-btn download-both" 
                    onClick={handleDownloadBoth}
                    data-testid="download-both"
                  >
                    <Download size={18} />
                    Download Both
                  </button>
                </div>
              </>
            ) : (
              <div className="preview-placeholder">
                <Image size={64} />
                <p>Your generated cover art will appear here</p>
                <p className="preview-hint">Two versions will be created: with and without AMR logo</p>
                <div className="preview-layout">
                  <div className="layout-indicator amr-logo">AMR</div>
                  <div className="layout-indicator text-area">
                    {textPosition === 'top_center' ? 'Text Here' : ''}
                  </div>
                  <div className="layout-indicator center-area">
                    {textPosition === 'center_center' ? 'Text Here' : ''}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoverArtGenerator;
